import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DeployOtaToDevice } from '../Components/device-details/DeployOtaToDevice';
import { DeviceDeploymentDetails } from '../Components/device-details/DeviceDeploymentDetails';
import { DeviceInfo } from '../Components/device-details/DeviceInfo';
import { DeviceSearch } from '../Components/device-details/DeviceSearch';
import { DeviceTags } from '../Components/device-details/DeviceTags';
import WelcomeBannerHome from '../Components/home/WelcomeBannerHome';
import {
  DeploymentStatus,
  useDeployToDeviceMutation,
  useGetDeploymentInfoByDeviceQuery,
  useGetDeploymentsMinQuery,
  useGetDeviceTagsQuery,
} from '../gql/generated';
import { OnSubmitModal } from './OnSubmitModal';

interface DeviceDetailsProps {
  canopyDeviceId: string;
  batteryLevel: number;
  deviceReportedVersion: string;
  lastConnected: string;
  lifecycleState: string;
  pendingOta: {
    status: string;
    version: string;
  };
  vehicle: {
    isInMotion: boolean | null;
    updatedAt: string;
  };
}

export default function DeviceDetails() {
  const [searchParams] = useSearchParams();
  const searchParamsSN = searchParams.get('serialNumber') || '';
  const [serialNumber, setSerialNumber] = useState(searchParamsSN);
  const [skipQuery, setSkipQuery] = useState<boolean>(
    serialNumber ? false : true,
  );
  const [deviceDetails, setDeviceDetails] = useState<DeviceDetailsProps>();
  const [modalState, setModalState] = useState({
    isVisible: false,
    title: '',
    message: '',
  });

  const [updatedDeviceDeployments, setUpdatedDeviceDeployments] =
    useState<any[]>();
  const [updatedDeployments, setUpdatedDeployments] = useState<any[]>([]);
  const [selectedDeployment, setSelectedDeployment] = useState<string>('');
  const { data, loading, error, refetch } = useGetDeploymentInfoByDeviceQuery({
    variables: {
      serialNumber: serialNumber,
    },
    skip: skipQuery,
    notifyOnNetworkStatusChange: true,
  });

  const { data: deploymentData, loading: deploymentLoading } =
    useGetDeploymentsMinQuery({
      variables: {
        status: DeploymentStatus.ACTIVE,
      },
    });

  const [deployToDevice, { loading: deployToDeviceLoading }] =
    useDeployToDeviceMutation();

  // need to invalidate cache for device tags query when serial number changes and when tag is disassociated
  const {
    data: deviceTagData,
    loading: deviceTagLoading,
    error: deviceTagError,
    refetch: refetchDeviceTags,
  } = useGetDeviceTagsQuery({
    variables: {
      input: {
        serialNumber: serialNumber,
        limit: 150,
      },
    },
    skip: skipQuery,
  });

  const onDeployToDevice = async () => {
    if (!selectedDeployment || !deviceDetails?.canopyDeviceId) {
      return;
    }
    await deployToDevice({
      variables: {
        input: {
          canopyDeviceId: deviceDetails.canopyDeviceId,
          deploymentId: selectedDeployment,
          serialNumber: serialNumber,
        },
      },
      onCompleted: () => {
        setModalState({
          isVisible: true,
          title: 'Success',
          message: 'OTA successfully requested',
        });
      },
      onError: (err) => {
        setModalState({
          isVisible: true,
          title: 'Error',
          message: err.message,
        });
      },
    });
  };

  const handleRefresh = async () => {
    await refetch();
  };
  const handleCloseModal = async () => {
    setModalState({ isVisible: false, title: '', message: '' });
    await refetch();
  };

  useMemo(() => {
    setDeviceDetails({
      canopyDeviceId: data?.getDeploymentInfoByDevice?.canopyDeviceId ?? '',
      batteryLevel: data?.getDeploymentInfoByDevice?.batteryLevel ?? 0,
      deviceReportedVersion:
        data?.getDeploymentInfoByDevice?.deviceReportedVersion ?? '?',
      lastConnected: data?.getDeploymentInfoByDevice?.lastConnected ?? '?',
      lifecycleState: data?.getDeploymentInfoByDevice?.lifecycleState ?? '',
      pendingOta: {
        status: data?.getDeploymentInfoByDevice?.pendingOta?.status ?? '',
        version: data?.getDeploymentInfoByDevice?.pendingOta?.version ?? '',
      },
      vehicle: {
        isInMotion:
          data?.getDeploymentInfoByDevice?.vehicle?.isInMotion ?? null,
        updatedAt: data?.getDeploymentInfoByDevice?.vehicle?.updatedAt ?? '',
      },
    });
    const deviceDeployments = data?.getDeploymentInfoByDevice?.deployments;
    if (deviceDeployments && deviceDeployments.length) {
      const deviceDeploymentsWorking = deviceDeployments?.map((deployment) => {
        return {
          version: deployment.version,
          jobId: deployment.jobId,
          status: deployment.status,
          states: deployment.states && {
            download: deployment.states?.download?.map((state) => {
              return { status: state?.status, updatedAt: state?.updatedAt };
            }),
            ...(deployment.states &&
              deployment.states.install && {
                install: deployment.states?.install.map((state) => {
                  return { status: state?.status, updatedAt: state?.updatedAt };
                }),
              }),
          },
        };
      });
      if (deviceDeploymentsWorking && deviceDeploymentsWorking.length) {
        setUpdatedDeviceDeployments(deviceDeploymentsWorking);
      }
    }
  }, [data]);
  useMemo(() => {
    const deployments = deploymentData?.getDeployments?.items;
    setUpdatedDeployments(deployments ?? []);
  }, [deploymentData]);

  return (
    <>
      <WelcomeBannerHome subtitle="Have fun looking up Device Details and stuff" />
      <div>
        <DeviceSearch
          serialNumber={serialNumber}
          setSerialNumber={setSerialNumber}
          loading={loading}
          setSkipQuery={setSkipQuery}
        />
      </div>
      <div>
        {skipQuery ? (
          <span style={{ marginLeft: '20%' }}>No Device Selected</span>
        ) : (
          <>
            {error ? (
              <div style={{ display: 'flex' }}>
                <div style={{ flexBasis: '20%' }}></div>
                <div
                  style={{
                    flexBasis: '80%',
                    paddingLeft: '20px',
                    color: 'red',
                    fontWeight: 'bold',
                  }}
                >
                  {error.message}
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex' }}>
                  <div style={{ flexBasis: '40%' }}></div>
                  <div style={{ flexBasis: '20%' }}>
                    <StyledButton
                      onPress={handleRefresh}
                      buttonRules={ButtonRules.primaryA}
                    >
                      Refresh
                    </StyledButton>
                  </div>
                  <div style={{ flexBasis: '40%' }}></div>
                </div>
                <div style={{ display: 'flex' }}>
                  <div style={{ flexBasis: '50%' }}>
                    <DeviceDeploymentDetails
                      loading={loading}
                      deployments={updatedDeviceDeployments}
                      canopyDeviceId={deviceDetails?.canopyDeviceId}
                      refetch={refetch}
                    />
                  </div>
                  <div style={{ flexBasis: '50%' }}>
                    <DeviceInfo
                      loading={loading}
                      deviceDetails={deviceDetails}
                    />
                    <DeployOtaToDevice
                      deploymentLoading={deploymentLoading}
                      deployToDeviceLoading={deployToDeviceLoading}
                      selectedDeployment={selectedDeployment}
                      setSelectedDeployment={setSelectedDeployment}
                      updatedDeployments={updatedDeployments}
                      onDeployToDevice={onDeployToDevice}
                    />
                    <DeviceTags
                      loading={deviceTagLoading}
                      data={deviceTagData?.getDeviceTags}
                      error={deviceTagError}
                      serialNumber={serialNumber}
                      refetch={refetchDeviceTags}
                    />
                  </div>
                </div>
                <OnSubmitModal
                  isVisible={modalState.isVisible}
                  onClose={handleCloseModal}
                  title={modalState.title}
                  message={modalState.message}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
